/**
 * Public vehicle search service — API + normalization + filters + sort (M3/M4).
 */
import { fetchClientViewVehicles } from "../api/clientViewVehicleApi";
import { DEV_SAMPLE_VEHICLE_LISTINGS_RAW } from "../data/devSampleVehicleListings";
import { normalizeVehicleListings } from "../utils/automapListing/normalizeVehicleListing";
import { shouldShowListingInSearch } from "../utils/automapListing/listingVisibility";
import {
  AUTOMAPA_PUBLIC_VIEW_SLUG,
  DEFAULT_SEARCH_PAGE_SIZE,
  RECOMMENDED_SEARCH_POOL_PAGE_SIZE,
} from "../constants/automapVehicleSearchConstants";
import { createDefaultVehicleSearchParams } from "../constants/vehicleSearchParamsConstants";
import { parseVehicleSearchParams } from "../utils/vehicleSearch/vehicleSearchParamsUrl";
import {
  applyVehicleSearchFilters,
  deriveAvailableFilters,
  getClientOnlyFilters,
  hasClientOnlyFilters,
} from "../utils/vehicleSearch/vehicleSearchFilters";
import {
  needsClientSideSort,
  needsDealerDiversitySort,
  sortVehicleListings,
} from "../utils/vehicleSearch/vehicleSearchSort";
import { buildFilteredResultStatusCopy } from "../utils/vehicleSearch/vehicleSearchFilterChips";
import { mapSortToApiSort } from "../constants/vehicleSearchParamsConstants";
import {
  buildPublicCacheKey,
  dedupeInFlightRequest,
} from "../utils/publicRequestCache";
import { buildVehicleSearchQueryKey } from "../utils/vehicleSearch/searchQueryKey";
import { expandFuelTypeFilterMatchTokens } from "../utils/vehicleSearch/vehicleSearchFuelType";

const DEV_FALLBACK_ENABLED =
  process.env.NODE_ENV !== "production" &&
  process.env.REACT_APP_AUTOMAPA_SEARCH_DEV_FALLBACK !== "false";

function logSearchDebug(message, meta = {}) {
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug(`[AutoMapa search] ${message}`, meta);
  }
}

/**
 * Map VehicleSearchParams → public vehicles API query keys.
 * TODO(M4+ backend): move client-only filters to server query.
 */
export function mapSearchParamsToApiQuery(params = {}, options = {}) {
  const p = parseVehicleSearchParams(params);
  const apiPageSize = needsDealerDiversitySort(p.sort)
    ? RECOMMENDED_SEARCH_POOL_PAGE_SIZE
    : p.pageSize;
  const query = {
    page: p.page,
    pageSize: apiPageSize,
    sort: mapSortToApiSort(p.sort),
  };

  const profileId = options.profileId ?? params.profile ?? params.profileId;
  if (profileId) query.profile = profileId;

  if (p.keyword) query.search = String(p.keyword).trim();
  if (p.city) query.city = p.city;
  if (p.department) query.department = p.department;
  if (p.make) query.make = p.make;
  if (p.model) query.model = p.model;
  if (p.yearMin != null) query.year_min = p.yearMin;
  if (p.yearMax != null) query.year_max = p.yearMax;
  if (p.priceMinCop != null) query.price_min = p.priceMinCop;
  if (p.priceMaxCop != null) query.price_max = p.priceMaxCop;
  if (p.mileageMinKm != null) query.mileage_min = p.mileageMinKm;
  if (p.mileageMaxKm != null) query.mileage_max = p.mileageMaxKm;
  if (p.transmission) query.transmission = p.transmission;
  if (p.fuelTypes?.length) {
    const fuelTokens = [
      ...new Set(p.fuelTypes.flatMap((fuelType) => expandFuelTypeFilterMatchTokens(fuelType))),
    ];
    query.fuel_types = fuelTokens.join(",");
  } else if (p.fuelType) {
    const fuelTokens = expandFuelTypeFilterMatchTokens(p.fuelType);
    if (fuelTokens.length > 1) query.fuel_types = fuelTokens.join(",");
    else query.fuel_type = fuelTokens[0] || p.fuelType;
  }
  if (p.bodyType) query.body_type = p.bodyType;
  if (p.dealerVerified) query.verified_only = "true";
  if (p.fairPriceLabels?.length === 1) query.fair_value_label = p.fairPriceLabels[0];
  if (p.fairPriceLabels?.includes("below_market") || p.fairPriceLabels?.includes("fair_price")) {
    query.below_fair_value_only = "true";
  }
  if (p.metroArea) query.metro_area = p.metroArea;
  if (p.requirePhotos) query.require_photos = "true";
  if (params.pageType) query.pageType = params.pageType;
  if (params.financing === true || params.financing === "true") query.financing = "true";

  return query;
}

function buildPostApiFilterParams(params) {
  const p = parseVehicleSearchParams(params);
  return {
    ...createDefaultVehicleSearchParams(),
    ...getClientOnlyFilters(p),
    metroArea: null,
    requirePhotos: false,
    hasPhotos: false,
  };
}

function paginateListings(listings, page, pageSize) {
  const total = listings.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    items: listings.slice(start, start + pageSize),
    pagination: {
      page: safePage,
      pageSize,
      total,
      totalPages,
      totalCount: total,
      hasMore: safePage < totalPages,
    },
  };
}

function processListingsPipeline(rawItems, searchParams, { apiSorted = false, fromApi = false } = {}) {
  const p = parseVehicleSearchParams(searchParams);
  const filterParams = fromApi ? buildPostApiFilterParams(searchParams) : p;
  const warnings = hasClientOnlyFilters(p) ? ["client_filters_applied"] : [];

  let listings = normalizeVehicleListings(rawItems).filter(shouldShowListingInSearch);
  listings = applyVehicleSearchFilters(listings, filterParams);

  if (needsClientSideSort(p.sort) || !apiSorted) {
    listings = sortVehicleListings(listings, p.sort, { keyword: p.keyword });
  }

  const availableFilters = deriveAvailableFilters(listings);
  let items;
  let pagination;
  if (fromApi) {
    items = listings;
    pagination = {
      page: p.page,
      pageSize: p.pageSize,
      total: listings.length,
      totalCount: listings.length,
      totalPages: Math.max(1, Math.ceil(listings.length / p.pageSize)),
      hasMore: false,
    };
  } else {
    ({ items, pagination } = paginateListings(listings, p.page, p.pageSize));
  }

  return {
    listings: items,
    totalCount: pagination.total,
    pagination,
    availableFilters,
    appliedFilters: p,
    sort: p.sort,
    warnings,
  };
}

function searchDevFallbackListings(searchParams) {
  const query = mapSearchParamsToApiQuery(searchParams);
  const rawMatches = DEV_SAMPLE_VEHICLE_LISTINGS_RAW.filter((raw) => {
    const listing = normalizeVehicleListings([raw])[0];
    if (!listing) return false;
    return applyVehicleSearchFilters([listing], searchParams).length > 0;
  });

  const processed = processListingsPipeline(rawMatches, searchParams);
  logSearchDebug("using development fallback inventory", {
    total: processed.totalCount,
    page: processed.pagination.page,
  });

  return {
    ok: true,
    ...processed,
    featured: null,
    emptyState: processed.totalCount === 0 ? { reason: "no_public_listings" } : null,
    source: "dev_fallback",
    devFallback: true,
    meta: { rawCount: DEV_SAMPLE_VEHICLE_LISTINGS_RAW.length, visibleCount: processed.listings.length },
  };
}

function processApiResponse(response, searchParams) {
  if (response?.success === false) {
    const error = new Error(response?.message || "Vehicle search request failed.");
    error.status = response?.status;
    throw error;
  }

  const rawItems = response?.items || response?.data?.items || [];
  const p = parseVehicleSearchParams(searchParams);
  const apiSorted = !needsClientSideSort(p.sort);
  const processed = processListingsPipeline(rawItems, searchParams, { apiSorted, fromApi: true });

  const apiPagination = response?.pagination;
  const clientVisibilityFilteredAll =
    rawItems.length > 0 && processed.listings.length === 0;
  if (apiPagination?.total != null && !clientVisibilityFilteredAll) {
    processed.pagination.total = apiPagination.total;
    processed.pagination.totalCount = apiPagination.total;
    processed.pagination.totalPages =
      apiPagination.totalPages ?? Math.ceil(apiPagination.total / p.pageSize);
    processed.pagination.hasMore = p.page < processed.pagination.totalPages;
    processed.totalCount = apiPagination.total;
  }

  return {
    ok: true,
    ...processed,
    featured: response?.featured || null,
    emptyState:
      response?.emptyState || (processed.listings.length === 0 ? { reason: "no_public_listings" } : null),
    source: "api",
    devFallback: false,
    meta: { rawCount: rawItems.length, visibleCount: processed.listings.length },
  };
}

/**
 * @param {string} slug
 * @param {import('../constants/vehicleSearchParamsConstants').VehicleSearchParams|object} params
 * @param {{ allowDevFallback?: boolean, profileId?: string }} [options]
 */
export async function searchPublicVehicleListings(
  slug = AUTOMAPA_PUBLIC_VIEW_SLUG,
  params = {},
  options = {}
) {
  const searchParams = parseVehicleSearchParams({
    ...params,
    pageSize: params.pageSize ?? DEFAULT_SEARCH_PAGE_SIZE,
    profile: options.profileId || params.profile,
  });
  const allowDevFallback = options.allowDevFallback ?? DEV_FALLBACK_ENABLED;
  const query = mapSearchParamsToApiQuery(searchParams, { profileId: options.profileId });
  const dedupeKey = buildPublicCacheKey([
    "search",
    slug,
    buildVehicleSearchQueryKey(slug, searchParams, options.profileId),
  ]);

  try {
    const response = await dedupeInFlightRequest(dedupeKey, () =>
      fetchClientViewVehicles(slug, query)
    );
    const result = processApiResponse(response, searchParams);
    logSearchDebug("inventory loaded", {
      source: result.source,
      ...result.meta,
      sort: result.sort,
      page: result.pagination?.page,
    });
    return result;
  } catch (error) {
    logSearchDebug("inventory fetch failed", { slug, message: error?.message });

    if (allowDevFallback) {
      return searchDevFallbackListings(searchParams);
    }

    return {
      ok: false,
      listings: [],
      totalCount: 0,
      pagination: null,
      appliedFilters: searchParams,
      availableFilters: null,
      sort: searchParams.sort,
      warnings: [],
      featured: null,
      emptyState: null,
      source: "error",
      devFallback: false,
      error,
      meta: { rawCount: 0, visibleCount: 0 },
    };
  }
}

export function dedupeListingsByCanonicalId(existing = [], incoming = []) {
  const ids = new Set(existing.map((item) => item.id || item.vehicleListingId));
  return [...existing, ...incoming.filter((item) => !ids.has(item.id || item.vehicleListingId))];
}

export function mergeAndSortVehicleListings(existing = [], incoming = [], sort, context = {}) {
  const merged = dedupeListingsByCanonicalId(existing, incoming);
  return sortVehicleListings(merged, sort, context);
}

/** @deprecated use buildFilteredResultStatusCopy */
export function buildSearchResultStatusCopy(filterState, pagination, visibleCount) {
  return buildFilteredResultStatusCopy(filterState, pagination, visibleCount);
}

/** @deprecated M4 — use applyVehicleSearchFilters via searchPublicVehicleListings */
export function applyClientSearchPostFilters(listings, clientFilters = {}) {
  return applyVehicleSearchFilters(listings, {
    hideHighRisk: clientFilters.hideRiskAlert,
    completeInfoOnly: clientFilters.completeInfoOnly,
    recentlyConfirmedOnly: clientFilters.availabilityConfirmed,
    riskLevels: clientFilters.lowRiskOnly ? ["low"] : [],
  });
}
